import React, { useEffect, useState, useRef, createContext, useContext } from 'react';
import { Layout, Menu, Button, message, Card, Typography, Modal, Tag, Descriptions, List, Input, Form, Row, Col, Drawer, Space, Upload } from 'antd';
import { LogoutOutlined, DashboardOutlined, TeamOutlined, UserOutlined, UserAddOutlined, MinusCircleOutlined, PlusOutlined, MenuOutlined, FileOutlined, MailOutlined, MessageOutlined, ApartmentOutlined, GiftOutlined, EditOutlined, UploadOutlined, CameraOutlined } from '@ant-design/icons';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { apiService } from '../services/api';
import { getCurrentUser, hasAuthority, getUserRole, hasAuthorities, clearAuthTokens } from '../utils/auth';
import { AgentInfo, AgentEventInfo } from '../types/auth';
import QRScanner from './QrScanner';
import type { UploadFile } from 'antd/es/upload/interface';

const { Header, Content, Sider } = Layout;
const { Title } = Typography;

const convertFileToBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result !== 'string') {
        reject(new Error('Failed to read file'));
        return;
      }
      const payload = reader.result.includes('base64,')
        ? reader.result.substring(reader.result.indexOf('base64,') + 'base64,'.length)
        : reader.result;
      resolve(payload);
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });

type AgentEditFormValues = {
  name: string;
  email: string;
  phone: string;
  licenseId: string;
  imageFile?: UploadFile[];
};

// Контекст для доступа к функциям открытия модальных окон
interface AdminDashboardContextType {
  openScannerModal: () => void;
}

const AdminDashboardContext = createContext<AdminDashboardContextType | null>(null);

export const useAdminDashboard = () => {
  const context = useContext(AdminDashboardContext);
  if (!context) {
    throw new Error('useAdminDashboard must be used within AdminDashboard');
  }
  return context;
};

const AdminDashboard: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Проверяем роль пользователя - мерчанты должны быть перенаправлены
  useEffect(() => {
    const userRole = getUserRole();
    if (userRole === 'merchant') {
      navigate('/merchant/event', { replace: true });
    }
  }, [navigate]);
  
  // Проверяем наличие authorities при монтировании компонента
  useEffect(() => {
    if (!hasAuthorities()) {
      // Если authorities отсутствуют, разавторизовываем пользователя
      clearAuthTokens();
      message.error('Access denied: no authorities found');
      navigate('/login', { replace: true });
    }
  }, [navigate]);
  
  // Получаем текущую страницу из URL
  const getCurrentPage = React.useCallback(() => {
    const path = location.pathname;
    if (path === '/admin/dashboard' || path === '/admin') {
      return 'dashboard';
    }
    if (path === '/admin/agents') {
      return 'agents';
    }
    if (path === '/admin/offer/merchants') {
      return 'offer/merchants';
    }
    if (path === '/admin/files') {
      return 'files';
    }
    if (path === '/admin/newsletter') {
      return 'newsletter';
    }
    if (path === '/admin/users') {
      return 'users';
    }
    if (path === '/admin/wa/chats') {
      return 'wa/chats';
    }
    if (path === '/admin/mbc/agents') {
      return 'mbc/agents';
    }
    if (path === '/admin/sf/units') {
      return 'sf/units';
    }
    if (path === '/admin/winter/kids') {
      return 'winter/kids';
    }
    if (path === '/admin/fajr/city') {
      return 'fajr/city';
    }
    return 'dashboard';
  }, [location.pathname]);

  const [selectedKey, setSelectedKey] = useState(() => {
    const path = location.pathname;
    if (path === '/admin/dashboard' || path === '/admin') {
      return 'dashboard';
    }
    if (path === '/admin/agents') {
      return 'agents';
    }
    if (path === '/admin/offer/merchants') {
      return 'offer/merchants';
    }
    if (path === '/admin/files') {
      return 'files';
    }
    if (path === '/admin/newsletter') {
      return 'newsletter';
    }
    if (path === '/admin/users') {
      return 'users';
    }
    if (path === '/admin/wa/chats') {
      return 'wa/chats';
    }
    if (path === '/admin/mbc/agents') {
      return 'mbc/agents';
    }
    if (path === '/admin/sf/units') {
      return 'sf/units';
    }
    if (path === '/admin/winter/kids') {
      return 'winter/kids';
    }
    if (path === '/admin/fajr/city') {
      return 'fajr/city';
    }
    return 'dashboard';
  });
  const [isScannerModalVisible, setIsScannerModalVisible] = useState(false);
  const [isAgentModalVisible, setIsAgentModalVisible] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState<AgentInfo | null>(null);
  const [agentModalLoading, setAgentModalLoading] = useState(false);
  const [isVisitorModalVisible, setIsVisitorModalVisible] = useState(false);
  const [isAddingVisitors, setIsAddingVisitors] = useState(false);
  const [visitorForm] = Form.useForm();
  const [editForm] = Form.useForm();
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [isSavingAgent, setIsSavingAgent] = useState(false);
  const [hasEditChanges, setHasEditChanges] = useState(false);
  const [cameraPhotoFile, setCameraPhotoFile] = useState<File | null>(null);
  const [cameraPhotoPreview, setCameraPhotoPreview] = useState<string | null>(null);
  const [isCameraCaptureVisible, setIsCameraCaptureVisible] = useState(false);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [pendingAgentImagePreview, setPendingAgentImagePreview] = useState<string | null>(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const scannerRef = useRef<{ stopScanner: () => void }>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const cameraVideoRef = useRef<HTMLVideoElement>(null);
  const cameraStreamRef = useRef<MediaStream | null>(null);
  const currentUser = getCurrentUser();
  
  // Синхронизируем selectedKey с URL при изменении location
  useEffect(() => {
    const currentPage = getCurrentPage();
    setSelectedKey(currentPage);
  }, [getCurrentPage]);
  
  // Force re-render when user changes by using state
  const [userAuthorities, setUserAuthorities] = useState<string[]>(() => {
    const user = getCurrentUser();
    return user?.authorities || [];
  });
  
  useEffect(() => {
    const user = getCurrentUser();
    if (user?.authorities) {
      setUserAuthorities(user.authorities);
      console.log('User authorities updated:', user.authorities);
      console.log('Has agents:*:', hasAuthority('agents:*'));
      console.log('Has newsletter:*:', hasAuthority('newsletter:*'));
      console.log('Has files:*:', hasAuthority('files:*'));
    } else {
      // Check localStorage directly if user is null
      const userStr = localStorage.getItem('currentUser');
      if (userStr) {
        try {
          const parsedUser = JSON.parse(userStr);
          if (parsedUser?.authorities) {
            setUserAuthorities(parsedUser.authorities);
            console.log('Loaded authorities from localStorage:', parsedUser.authorities);
          }
        } catch (e) {
          console.error('Failed to parse user from localStorage:', e);
        }
      }
    }
  }, []);

  useEffect(() => {
    return () => {
      if (cameraPhotoPreview) {
        URL.revokeObjectURL(cameraPhotoPreview);
      }
    };
  }, [cameraPhotoPreview]);

  useEffect(() => {
    return () => {
      if (pendingAgentImagePreview) {
        URL.revokeObjectURL(pendingAgentImagePreview);
      }
    };
  }, [pendingAgentImagePreview]);

  useEffect(() => {
    return () => {
      if (cameraStreamRef.current) {
        cameraStreamRef.current.getTracks().forEach((track) => track.stop());
        cameraStreamRef.current = null;
      }
    };
  }, []);

  const handleLogout = async () => {
    try {
      await apiService.logoutUser();
      navigate('/login');
    } catch (error) {
      message.error('Logout failed');
    }
  };

  const handleMenuClick = (key: string) => {
    setSelectedKey(key);
    // Навигация по URL вместо сохранения в localStorage
    navigate(`/admin/${key}`);
  };

  const handleScan = async (result: string) => {
    try {
      // Extract agent ID from QR code result
      const agentId = result.trim();
      
      // Get agent information
      setAgentModalLoading(true);
      const agentInfo = await apiService.getAgentInfo(agentId);
      
      // Проверяем состояние агента и показываем предупреждения
      if (agentInfo.agent.state === 'deleted') {
        message.warning('Agent is deleted. Actions are limited.');
      } else if (agentInfo.agent.state === 'blocked') {
        message.warning('Agent is blocked. Some actions may be limited.');
      }
      
      setSelectedAgent(agentInfo);
      setIsAgentModalVisible(true);
      setIsScannerModalVisible(false);
      
      // Stop scanner
      if (scannerRef.current) {
        scannerRef.current.stopScanner();
      }
      
      message.success('Agent found successfully!');
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to find agent');
    } finally {
      setAgentModalLoading(false);
    }
  };

  const handleError = (error: Error) => {
    message.error(`Scanner error: ${error.message}`);
  };

  const handleScannerModalClose = () => {
    if (scannerRef.current) {
      scannerRef.current.stopScanner();
    }
    setIsScannerModalVisible(false);
  };

  const handleAgentModalClose = () => {
    stopCameraCapture();
    setIsCameraCaptureVisible(false);
    setIsAgentModalVisible(false);
    setIsEditModalVisible(false);
    setHasEditChanges(false);
    setCameraPhotoFile(null);
    setCameraPhotoPreview(null);
    setPendingAgentImagePreview(null);
    editForm.resetFields();
    setSelectedAgent(null);
  };

  const handleOpenEditAgent = () => {
    if (!selectedAgent) return;
    editForm.setFieldsValue({
      name: selectedAgent.agent.name,
      email: selectedAgent.agent.email,
      phone: selectedAgent.agent.phone ?? '',
      licenseId: selectedAgent.agent.licenseId,
      imageFile: [],
    });
    setCameraPhotoFile(null);
    setCameraPhotoPreview(null);
    setHasEditChanges(false);
    setIsEditModalVisible(true);
  };

  const handleCloseEditAgent = () => {
    if (isSavingAgent) return;
    stopCameraCapture();
    setIsCameraCaptureVisible(false);
    setIsEditModalVisible(false);
    setHasEditChanges(false);
    setCameraPhotoFile(null);
    setCameraPhotoPreview(null);
    editForm.resetFields();
  };

  const handleEditValuesChange = (_changed: unknown, allValues: AgentEditFormValues) => {
    if (!selectedAgent) return;
    const hasUploadImage = Array.isArray(allValues.imageFile) && allValues.imageFile.length > 0;
    const hasChanges =
      allValues.name !== selectedAgent.agent.name ||
      allValues.email !== selectedAgent.agent.email ||
      (allValues.phone ?? '').trim() !== (selectedAgent.agent.phone ?? '').trim() ||
      allValues.licenseId !== selectedAgent.agent.licenseId ||
      hasUploadImage ||
      cameraPhotoFile !== null;
    setHasEditChanges(hasChanges);
  };

  const handleCameraFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !selectedAgent) {
      return;
    }
    setCameraPhotoFile(file);
    setCameraPhotoPreview(URL.createObjectURL(file));
    editForm.setFieldValue('imageFile', []);

    const values = editForm.getFieldsValue();
    const hasUploadImage = Array.isArray(values.imageFile) && values.imageFile.length > 0;
    const hasChanges =
      values.name !== selectedAgent.agent.name ||
      values.email !== selectedAgent.agent.email ||
      (values.phone ?? '').trim() !== (selectedAgent.agent.phone ?? '').trim() ||
      values.licenseId !== selectedAgent.agent.licenseId ||
      hasUploadImage ||
      file != null;
    setHasEditChanges(hasChanges);
    event.target.value = '';
  };

  const stopCameraCapture = () => {
    if (cameraStreamRef.current) {
      cameraStreamRef.current.getTracks().forEach((track) => track.stop());
      cameraStreamRef.current = null;
    }
    if (cameraVideoRef.current) {
      cameraVideoRef.current.srcObject = null;
    }
  };

  const handleOpenCameraCapture = async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      cameraInputRef.current?.click();
      return;
    }
    setCameraError(null);
    setIsCameraCaptureVisible(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: 'environment' } },
        audio: false
      });
      cameraStreamRef.current = stream;
      if (cameraVideoRef.current) {
        cameraVideoRef.current.srcObject = stream;
        await cameraVideoRef.current.play();
      }
    } catch {
      setCameraError('Unable to access camera. Check browser permissions.');
      stopCameraCapture();
    }
  };

  const handleCloseCameraCapture = () => {
    stopCameraCapture();
    setIsCameraCaptureVisible(false);
  };

  const handleCapturePhoto = async () => {
    const video = cameraVideoRef.current;
    if (!video) return;
    const width = video.videoWidth;
    const height = video.videoHeight;
    if (!width || !height) {
      setCameraError('Camera is not ready yet.');
      return;
    }
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      setCameraError('Unable to capture image.');
      return;
    }
    ctx.drawImage(video, 0, 0, width, height);
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'));
    if (!blob) {
      setCameraError('Unable to capture image.');
      return;
    }
    const file = new File([blob], `agent-camera-${Date.now()}.png`, { type: 'image/png' });
    setCameraPhotoFile(file);
    setCameraPhotoPreview(URL.createObjectURL(file));
    editForm.setFieldValue('imageFile', []);
    if (selectedAgent) {
      const values = editForm.getFieldsValue();
      const hasChanges =
        values.name !== selectedAgent.agent.name ||
        values.email !== selectedAgent.agent.email ||
        (values.phone ?? '').trim() !== (selectedAgent.agent.phone ?? '').trim() ||
        values.licenseId !== selectedAgent.agent.licenseId ||
        file != null;
      setHasEditChanges(hasChanges);
    }
    handleCloseCameraCapture();
  };

  const handleSaveAgent = async (values: AgentEditFormValues) => {
    if (!selectedAgent) return;
    const selectedImageFile = cameraPhotoFile ?? values.imageFile?.[0]?.originFileObj;
    const hasNewImage = Boolean(selectedImageFile);

    const hasChanges =
      values.name !== selectedAgent.agent.name ||
      values.email !== selectedAgent.agent.email ||
      (values.phone ?? '').trim() !== (selectedAgent.agent.phone ?? '').trim() ||
      values.licenseId !== selectedAgent.agent.licenseId ||
      hasNewImage;
    if (!hasChanges) {
      message.info('No changes detected');
      return;
    }

    setIsSavingAgent(true);
    let localPreviewUrl: string | undefined;
    try {
      let imageBase64: string | undefined;
      if (selectedImageFile) {
        localPreviewUrl = URL.createObjectURL(selectedImageFile);
        imageBase64 = await convertFileToBase64(selectedImageFile);
      }
      const trimmedLicenseId = (values.licenseId ?? '').trim();
      const prevLicenseId = (selectedAgent.agent.licenseId ?? '').trim();
      const trimmedPhone = (values.phone ?? '').trim();
      const prevPhone = (selectedAgent.agent.phone ?? '').trim();
      const payload: Record<string, string> = {};
      if (values.name !== selectedAgent.agent.name) {
        payload.name = values.name;
      }
      if (values.email !== selectedAgent.agent.email) {
        payload.email = values.email;
      }
      if (trimmedPhone !== prevPhone && trimmedPhone.length > 0) {
        payload.phone = trimmedPhone;
      }
      if (trimmedLicenseId !== prevLicenseId && trimmedLicenseId.length > 0) {
        payload.licenseId = trimmedLicenseId;
      }
      if (imageBase64) {
        payload.imageBase64 = imageBase64;
      }
      const response = await apiService.updateAgent(selectedAgent.agent.id, payload);
      if (!response.success) {
        if (localPreviewUrl) {
          URL.revokeObjectURL(localPreviewUrl);
        }
        message.error(response.msg || 'Failed to update agent');
        return;
      }
      const nextAgent = localPreviewUrl
        ? { ...response.body, imageLink: localPreviewUrl, imageUrl: localPreviewUrl }
        : response.body;
      if (localPreviewUrl) {
        if (pendingAgentImagePreview) {
          URL.revokeObjectURL(pendingAgentImagePreview);
        }
        setPendingAgentImagePreview(localPreviewUrl);
      }
      const updatedAgent = {
        ...selectedAgent,
        agent: nextAgent
      };
      setSelectedAgent(updatedAgent);
      setIsEditModalVisible(false);
      setHasEditChanges(false);
      setCameraPhotoFile(null);
      setCameraPhotoPreview(null);
      editForm.resetFields();
      message.success(hasNewImage ? 'Agent updated. Photo upload has been queued.' : (response.msg || 'Agent updated'));
    } catch (error: any) {
      if (localPreviewUrl) {
        URL.revokeObjectURL(localPreviewUrl);
      }
      const errorMsg = error.response?.data?.msg || error.response?.data?.message || 'Failed to update agent';
      message.error(errorMsg);
    } finally {
      setIsSavingAgent(false);
    }
  };

  const handleAgentVisited = () => {
    visitorForm.resetFields();
    visitorForm.setFieldsValue({
      visitors: [{ name: '', phone: '' }]
    });
    setIsVisitorModalVisible(true);
  };

  const handleVisitorModalClose = () => {
    setIsVisitorModalVisible(false);
    visitorForm.resetFields();
    setIsAddingVisitors(false);
  };

  const handleAddVisitors = async (values: { visitors: Array<{ name: string; phone: string }> }) => {
    if (!selectedAgent) return;
    
    // Проверяем, не удален ли агент
    if (selectedAgent.agent.state === 'deleted') {
      message.error('Cannot add visitors to deleted agent');
      return;
    }
    
    // Проверяем, не заблокирован ли агент
    if (selectedAgent.agent.state === 'blocked') {
      message.error('Cannot add visitors to blocked agent');
      return;
    }
    
    setIsAddingVisitors(true);
    
    try {
      // Filter out visitors with empty names or phones
      const validVisitors = values.visitors.filter(visitor => visitor.name?.trim() || visitor.phone?.trim());
      
      const eventData = {
        guests: validVisitors
      };
      
      await apiService.addAgentEvent(selectedAgent.agent.id, eventData);
      message.success('Visitors added successfully');
      
      const updatedAgentInfo = await apiService.getAgentInfo(selectedAgent.agent.id);
      setSelectedAgent(updatedAgentInfo);
      
      setIsVisitorModalVisible(false);
      visitorForm.resetFields();
    } catch (error: any) {
      message.error('Failed to add visitors');
    } finally {
      setIsAddingVisitors(false);
    }
  };

  const handleBlockAgent = async (agentId: string, agentName: string, currentState: string) => {
    if (currentState === 'deleted') {
      message.error('Cannot perform actions on deleted agent');
      return;
    }

    const isCurrentlyBlocked = currentState === 'blocked';
    const action = isCurrentlyBlocked ? 'unblock' : 'block';

    Modal.confirm({
      title: `Confirm ${action.charAt(0).toUpperCase() + action.slice(1)}`,
      content: `Are you sure you want to ${action} agent "${agentName}"?`,
      okText: action.charAt(0).toUpperCase() + action.slice(1),
      okType: isCurrentlyBlocked ? 'default' : 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await apiService.blockAgent(agentId, !isCurrentlyBlocked);
          const nextState = (isCurrentlyBlocked ? 'active' : 'blocked') as 'active' | 'blocked';
          setSelectedAgent((prev) => {
            if (!prev || prev.agent.id !== agentId) return prev;
            return {
              ...prev,
              agent: {
                ...prev.agent,
                state: nextState,
                blockReason: isCurrentlyBlocked ? undefined : prev.agent.blockReason
              }
            };
          });
          message.success(`Agent ${action}ed successfully`);
        } catch (error: any) {
          if (error.response?.status === 401) {
            navigate('/login');
          } else if (error.response?.status === 403) {
            message.error('Access denied. You do not have permission to block/unblock this agent.');
          } else {
            message.error(`Failed to ${action} agent`);
          }
        }
      },
    });
  };

  const formatDateTime = (dateTime: string) => {
    return new Date(dateTime).toLocaleString();
  };

  return (
    <Layout style={{
      minHeight: '100vh',
      ...(selectedKey === 'wa/chats' ? { height: '100vh', overflow: 'hidden' } : {}),
    }}>
      {/* Desktop Sider */}
      <Sider 
        width={200} 
        className="desktop-sider"
        style={{ display: window.innerWidth <= 768 ? 'none' : 'block' }}
      >
        <div style={{ 
          height: 32, 
          margin: 16, 
          background: 'rgba(255, 255, 255, 0.3)',
          borderRadius: 4,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          fontWeight: 'bold'
        }}>
          Admin Office
        </div>
        <Menu
          mode="inline"
          selectedKeys={[selectedKey]}
          style={{ height: '100%', borderRight: 0 }}
          theme="dark"
          onClick={({ key }) => handleMenuClick(key)}
        >
          <Menu.Item key="dashboard" icon={<DashboardOutlined />}>
            Dashboard
          </Menu.Item>
          {userAuthorities.length > 0 && hasAuthority('offer:agents:*') && (
            <Menu.SubMenu key="offer" title="Offer" icon={<TeamOutlined />}>
              <Menu.Item key="agents" icon={<TeamOutlined />}>
                Agents
              </Menu.Item>
              <Menu.Item key="offer/merchants" icon={<TeamOutlined />}>
                Merchants
              </Menu.Item>
            </Menu.SubMenu>
          )}
          {userAuthorities.length > 0 && (hasAuthority('newsletter:*') || hasAuthority('files:*')) && (
            <Menu.SubMenu key="office" title="Office" icon={<MailOutlined />}>
              {hasAuthority('newsletter:*') && (
                <Menu.Item key="newsletter" icon={<MailOutlined />}>
                  Newsletter
                </Menu.Item>
              )}
              {hasAuthority('files:*') && (
                <Menu.Item key="files" icon={<FileOutlined />}>
                  Files
                </Menu.Item>
              )}
            </Menu.SubMenu>
          )}
          {userAuthorities.length > 0 && hasAuthority('wa_chats:*') && (
            <Menu.SubMenu key="wa" title="WA" icon={<MessageOutlined />}>
              <Menu.Item key="wa/chats" icon={<MessageOutlined />}>
                Chats
              </Menu.Item>
            </Menu.SubMenu>
          )}
          {userAuthorities.length > 0 && hasAuthority('mbc:*') && (
            <Menu.SubMenu key="mbc" title="MBC" icon={<TeamOutlined />}>
              <Menu.Item key="mbc/agents" icon={<TeamOutlined />}>
                Agents
              </Menu.Item>
            </Menu.SubMenu>
          )}
          {userAuthorities.length > 0 && hasAuthority('users:*') && (
            <Menu.SubMenu key="general" title="General" icon={<UserOutlined />}>
              <Menu.Item key="users" icon={<UserOutlined />}>
                Users
              </Menu.Item>
            </Menu.SubMenu>
          )}
          {userAuthorities.length > 0 && hasAuthority('sf_units:*') && (
            <Menu.SubMenu key="sf" title="SF" icon={<ApartmentOutlined />}>
              <Menu.Item key="sf/units" icon={<ApartmentOutlined />}>
                Units
              </Menu.Item>
            </Menu.SubMenu>
          )}
          {userAuthorities.length > 0 && hasAuthority('winter_offer:*') && (
            <Menu.SubMenu key="winter" title="Winter" icon={<GiftOutlined />}>
              <Menu.Item key="winter/kids" icon={<GiftOutlined />}>
                Kids
              </Menu.Item>
            </Menu.SubMenu>
          )}
          {userAuthorities.length > 0 && hasAuthority('fajr_city:*') && (
            <Menu.Item key="fajr/city" icon={<TeamOutlined />}>
              Mira Hills
            </Menu.Item>
          )}
        </Menu>
      </Sider>
      
      <Layout style={selectedKey === 'wa/chats' ? { flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' } : undefined}>
        <Header style={{ 
          background: '#fff', 
          padding: '0 16px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <Button 
              type="text" 
              icon={<MenuOutlined />} 
              onClick={() => setIsMobileMenuOpen(true)}
              className="mobile-menu-toggle"
              style={{ marginRight: 8 }}
            />
            <Title level={3} style={{ margin: 0, fontSize: window.innerWidth <= 768 ? '18px' : '24px' }}>
              {selectedKey === 'agents' ? 'Agents' : 
               selectedKey === 'offer/merchants' ? 'Offer - Merchants' :
               selectedKey === 'files' ? 'Files' : 
               selectedKey === 'newsletter' ? 'Newsletter' : 
               selectedKey === 'users' ? 'Users' : 
               selectedKey === 'wa/chats' ? 'WA Chats' : 
               selectedKey === 'mbc/agents' ? 'MBC Agents' : 
               selectedKey === 'sf/units' ? 'SF Units' : 
               selectedKey === 'winter/kids' ? 'Winter Offer - Kids' :
               selectedKey === 'fajr/city' ? 'Mira Hills' : 'Admin Dashboard'}
            </Title>
            {currentUser && window.innerWidth > 768 && (
              <span style={{ color: '#666' }}>
                Welcome, {currentUser.username} ({currentUser.role})
              </span>
            )}
          </div>
          <Button 
            type="text" 
            icon={<LogoutOutlined />} 
            onClick={handleLogout}
          >
            Logout
          </Button>
        </Header>
        
        <Content
          className={selectedKey === 'wa/chats' ? 'wa-chats-page-content' : undefined}
          style={{
            ...(selectedKey === 'wa/chats'
              ? {
                  margin: 0,
                  padding: 0,
                  flex: 1,
                  minHeight: 0,
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                  background: '#fff',
                }
              : {
                  margin: window.innerWidth <= 768 ? '16px 8px' : '24px 16px',
                  padding: window.innerWidth <= 768 ? 16 : 24,
                  background: '#fff',
                }),
          }}
        >
          <AdminDashboardContext.Provider value={{ openScannerModal: () => setIsScannerModalVisible(true) }}>
            <Outlet />
          </AdminDashboardContext.Provider>
        </Content>
      </Layout>

      {/* QR Scanner Modal */}
      <Modal
        title="QR Code Scanning"
        open={isScannerModalVisible}
        onCancel={handleScannerModalClose}
        footer={null}
        width={600}
        bodyStyle={{ padding: '16px', height: '400px' }}
      >
        <QRScanner 
          ref={scannerRef}
          onResult={handleScan} 
          onError={handleError} 
          isActive={isScannerModalVisible}
        />
      </Modal>

      {/* Agent Details Modal */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <UserOutlined />
            Agent Details
          </div>
        }
        open={isAgentModalVisible}
        onCancel={handleAgentModalClose}
        footer={[
          <Button key="edit" icon={<EditOutlined />} onClick={handleOpenEditAgent} disabled={!selectedAgent}>
            Edit
          </Button>,
          <Button key="close" onClick={handleAgentModalClose}>
            Close
          </Button>
        ]}
        width={800}
        loading={agentModalLoading}
      >
        {selectedAgent && (
          <div>
            <div style={{ 
              display: 'flex', 
              gap: 24, 
              marginBottom: 24,
              flexDirection: window.innerWidth <= 768 ? 'column' : 'row',
              alignItems: window.innerWidth <= 768 ? 'center' : 'flex-start'
            }}>
              {/* Фотографии агента */}
              <div style={{ 
                flexShrink: 0,
                textAlign: 'center',
                order: window.innerWidth <= 768 ? 1 : 1
              }}>
                {(() => {
                  const agentImages = [
                    { imageSrc: selectedAgent.agent.imageUrl || selectedAgent.agent.imageLink, badge: undefined },
                    { imageSrc: selectedAgent.agent.reraImage, badge: 'Image from license' }
                  ]
                    .filter((entry): entry is { imageSrc: string; badge?: string } => Boolean(entry.imageSrc))
                    .filter((entry, index, array) => array.findIndex((candidate) => candidate.imageSrc === entry.imageSrc) === index);

                  if (agentImages.length === 0) {
                    return (
                  <div style={{ 
                    width: window.innerWidth <= 768 ? '200px' : '250px', 
                    height: window.innerWidth <= 768 ? '200px' : '250px',
                    border: '3px dashed #d9d9d9',
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#fafafa'
                  }}>
                    <span style={{ 
                      color: '#999', 
                      fontStyle: 'italic',
                      fontSize: '16px',
                      textAlign: 'center'
                    }}>
                      No photo<br/>available
                    </span>
                  </div>
                    );
                  }

                  return (
                    <div>
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 12,
                          alignItems: 'center',
                        }}
                      >
                        {agentImages.map(({ imageSrc, badge }, index) => (
                          <div key={`${imageSrc}-${index}`}>
                            <img
                              src={imageSrc}
                              alt={`${selectedAgent.agent.name} ${index + 1}`}
                              style={{ 
                                width: window.innerWidth <= 768 ? '200px' : '250px', 
                                height: window.innerWidth <= 768 ? '200px' : '250px', 
                                objectFit: 'cover',
                                borderRadius: '12px',
                                cursor: 'pointer',
                                border: '3px solid #d9d9d9',
                                boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                              }}
                              onClick={() => {
                                Modal.info({
                                  title: `${selectedAgent.agent.name} Photo ${index + 1}`,
                                  content: (
                                    <div style={{ textAlign: 'center' }}>
                                      <img
                                        src={imageSrc}
                                        alt={`${selectedAgent.agent.name} ${index + 1}`}
                                        style={{ 
                                          maxWidth: '100%', 
                                          maxHeight: '600px', 
                                          objectFit: 'contain',
                                          borderRadius: '8px'
                                        }}
                                      />
                                    </div>
                                  ),
                                  width: 800,
                                  okText: 'Close'
                                });
                              }}
                            />
                            {badge && (
                              <div style={{ marginTop: 6, fontSize: '12px', color: '#666', fontWeight: 500 }}>
                                {badge}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}
              </div>

              {/* Информация об агенте */}
              <div style={{ 
                flex: 1,
                order: window.innerWidth <= 768 ? 2 : 2
              }}>
                                <Descriptions 
                  title="Agent Information" 
                  bordered 
                  column={2}
                  size="small"
                  labelStyle={{ 
                    fontWeight: 'bold',
                    padding: '8px 12px',
                    backgroundColor: '#fafafa'
                  }}
                  contentStyle={{ 
                    padding: '8px 12px'
                  }}
                >
                  <Descriptions.Item label="Name" span={2}>
                    <span style={{ fontSize: '16px', fontWeight: '500' }}>
                      {selectedAgent.agent.name}
                    </span>
                  </Descriptions.Item>
                  {selectedAgent.agent.reraName && (
                    <Descriptions.Item label="License Name" span={2}>
                      <span style={{ fontSize: '14px' }}>
                        {selectedAgent.agent.reraName}
                      </span>
                    </Descriptions.Item>
                  )}
                  
                  <Descriptions.Item label="ID" span={2}>
                    <span style={{ 
                      fontFamily: 'monospace', 
                      fontSize: '13px',
                      backgroundColor: '#f5f5f5',
                      padding: '3px 6px',
                      borderRadius: '3px',
                      border: '1px solid #d9d9d9',
                      display: 'inline-block',
                      width: 'fit-content'
                    }}>
                      {selectedAgent.agent.id}
                    </span>
                  </Descriptions.Item>
                  
                  <Descriptions.Item label="License" span={2}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ 
                          fontFamily: 'monospace', 
                          fontSize: '13px',
                          backgroundColor: '#e6f7ff',
                          padding: '3px 6px',
                          borderRadius: '3px',
                          border: '1px solid #91d5ff',
                          display: 'inline-block',
                          width: 'fit-content',
                          color: '#1890ff'
                        }}>
                          {selectedAgent.agent.licenseId}
                        </span>
                        {selectedAgent.agent.reraVerified !== undefined && (
                          <Tag color={selectedAgent.agent.reraVerified ? 'green' : 'red'}>
                            {selectedAgent.agent.reraVerified ? 'Verified' : 'Not Verified'}
                          </Tag>
                        )}
                      </div>
                      {selectedAgent.agent.reraVerified === false && (
                        <div style={{ color: '#ff4d4f', fontSize: '12px', fontWeight: '500' }}>
                          Hostess visit required
                        </div>
                      )}
                    </div>
                  </Descriptions.Item>
                  
                  <Descriptions.Item label="Company" span={2}>
                    <span style={{ fontSize: '14px' }}>
                      {selectedAgent.agent.company}
                    </span>
                  </Descriptions.Item>
                  
                  <Descriptions.Item label="Email" span={2}>
                    <span style={{ fontSize: '14px', color: '#1890ff' }}>
                      {selectedAgent.agent.email}
                    </span>
                  </Descriptions.Item>
                  
                  <Descriptions.Item label="Phone" span={2}>
                    <span style={{ fontSize: '14px' }}>
                      {selectedAgent.agent.phone}
                    </span>
                  </Descriptions.Item>
                  
                  <Descriptions.Item label="Registration Date" span={2}>
                    <span style={{ fontSize: '13px', color: '#666' }}>
                      {formatDateTime(selectedAgent.agent.createdAt)}
                    </span>
                  </Descriptions.Item>
                </Descriptions>
              </div>
            </div>
            
            {/* Agent Status Block */}
            {(selectedAgent.agent.state === 'deleted' || selectedAgent.agent.state === 'blocked') && (
              <Card 
                style={{ 
                  marginTop: 16, 
                  marginBottom: 16,
                  borderColor: '#ff4d4f',
                  backgroundColor: '#fff2f0'
                }}
                size="small"
              >
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 12,
                  color: '#ff4d4f'
                }}>
                  <div style={{ 
                    fontSize: '18px', 
                    fontWeight: 'bold' 
                  }}>
                    {selectedAgent.agent.state === 'deleted' ? 'Agent Deleted' : 'Agent Blocked'}
                  </div>
                  {selectedAgent.agent.blockReason && (
                    <div style={{ 
                      fontSize: '14px',
                      color: '#666',
                      fontStyle: 'italic'
                    }}>
                      Reason: {selectedAgent.agent.blockReason}
                    </div>
                  )}
                </div>
              </Card>
            )}
            
            <div style={{ marginTop: 24 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                <h3>Events History ({selectedAgent.events.length})</h3>
                <div style={{ display: 'flex', gap: 8 }}>
                  {selectedAgent.agent.state !== 'deleted' && (
                    <Button
                      type={selectedAgent.agent.state === 'blocked' ? 'default' : 'primary'}
                      danger={selectedAgent.agent.state !== 'blocked'}
                      onClick={() => handleBlockAgent(selectedAgent.agent.id, selectedAgent.agent.name, selectedAgent.agent.state)}
                    >
                      {selectedAgent.agent.state === 'blocked' ? 'Unblock Agent' : 'Block Agent'}
                    </Button>
                  )}
                  <Button
                    type="primary"
                    icon={<UserAddOutlined />}
                    onClick={handleAgentVisited}
                    disabled={selectedAgent.agent.reraVerified === false}
                  >
                    Agent Visited
                  </Button>
                </div>
              </div>
              <List
                dataSource={selectedAgent.events}
                renderItem={(event: AgentEventInfo) => (
                  <List.Item>
                    <List.Item.Meta
                      title={
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <Tag color="blue">{event.event.toUpperCase()}</Tag>
                          <span>{formatDateTime(event.dateTime)}</span>
                        </div>
                      }
                      description={
                        event.amount && event.amount > 0
                          ? `${event.merchantName ? `Merchant: ${event.merchantName} | ` : ''}Amount AED: ${event.amount}`
                          : (event.merchantName ? `Merchant: ${event.merchantName}` : 'No merchant information')
                      }
                    />
                  </List.Item>
                )}
                locale={{
                  emptyText: 'No events recorded for this agent'
                }}
              />
            </div>
          </div>
        )}
      </Modal>

      <Modal
        title="Edit Agent"
        open={isEditModalVisible}
        onCancel={handleCloseEditAgent}
        footer={[
          <Button key="cancel" onClick={handleCloseEditAgent} disabled={isSavingAgent}>
            Cancel
          </Button>,
          <Button
            key="save"
            type="primary"
            loading={isSavingAgent}
            disabled={!hasEditChanges}
            onClick={() => editForm.submit()}
          >
            Save
          </Button>
        ]}
        width={720}
      >
        {selectedAgent && (
          <Form
            form={editForm}
            layout="vertical"
            onFinish={handleSaveAgent}
            initialValues={{
              name: selectedAgent.agent.name,
              email: selectedAgent.agent.email,
              phone: selectedAgent.agent.phone ?? '',
              licenseId: selectedAgent.agent.licenseId,
              imageFile: [],
            }}
            onValuesChange={handleEditValuesChange}
          >
            <Form.Item
              label="Name"
              name="name"
              rules={[
                { required: true, message: 'Name is required' },
                { min: 2, message: 'Name must be at least 2 characters long' }
              ]}
            >
              <Input disabled={isSavingAgent} />
            </Form.Item>
            <Form.Item
              label="Email"
              name="email"
              rules={[
                { required: true, message: 'Email is required' },
                { type: 'email', message: 'Please enter a valid email' }
              ]}
            >
              <Input disabled={isSavingAgent} />
            </Form.Item>
            <Form.Item
              label="Phone"
              name="phone"
              rules={[
                { required: true, message: 'Phone is required' },
                { max: 128, message: 'Phone cannot exceed 128 characters' }
              ]}
            >
              <Input placeholder="Enter phone" disabled={isSavingAgent} />
            </Form.Item>
            <Form.Item
              label="License ID"
              name="licenseId"
            >
              <Input disabled={isSavingAgent} />
            </Form.Item>
            <Form.Item
              label="Upload Photo"
              name="imageFile"
              valuePropName="fileList"
              getValueFromEvent={(event) => {
                if (Array.isArray(event)) {
                  return event;
                }
                return event?.fileList;
              }}
            >
              <Upload
                accept="image/*"
                maxCount={1}
                beforeUpload={() => false}
                listType="picture"
                onChange={(info) => {
                  if (info.fileList.length > 0) {
                    setCameraPhotoFile(null);
                    setCameraPhotoPreview(null);
                  }
                }}
              >
                <Button icon={<UploadOutlined />} disabled={isSavingAgent}>
                  Select Image
                </Button>
              </Upload>
            </Form.Item>
            <Form.Item label="Take Photo">
              <Space direction="vertical" size={8}>
                <Button
                  icon={<CameraOutlined />}
                  onClick={handleOpenCameraCapture}
                  disabled={isSavingAgent}
                >
                  Open Camera
                </Button>
                <input
                  ref={cameraInputRef}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  style={{ display: 'none' }}
                  onChange={handleCameraFileChange}
                />
                {cameraPhotoPreview && (
                  <img
                    src={cameraPhotoPreview}
                    alt="Camera preview"
                    style={{
                      width: 180,
                      height: 180,
                      objectFit: 'cover',
                      borderRadius: 8,
                      border: '1px solid #d9d9d9'
                    }}
                  />
                )}
                {cameraError && (
                  <span style={{ color: '#ff4d4f', fontSize: 12 }}>{cameraError}</span>
                )}
              </Space>
            </Form.Item>
          </Form>
        )}
      </Modal>

      <Modal
        title="Camera Capture"
        open={isCameraCaptureVisible}
        onCancel={handleCloseCameraCapture}
        footer={[
          <Button key="cancel" onClick={handleCloseCameraCapture}>
            Cancel
          </Button>,
          <Button key="capture" type="primary" icon={<CameraOutlined />} onClick={handleCapturePhoto}>
            Capture
          </Button>
        ]}
        width={520}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <video
            ref={cameraVideoRef}
            autoPlay
            playsInline
            muted
            style={{ width: '100%', borderRadius: 8, background: '#000' }}
          />
          {cameraError && <span style={{ color: '#ff4d4f', fontSize: 12 }}>{cameraError}</span>}
        </div>
      </Modal>

      {/* Visitor Modal */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <UserAddOutlined />
            Agent Visited
          </div>
        }
        open={isVisitorModalVisible}
        onCancel={handleVisitorModalClose}
        footer={[
          <Button key="cancel" onClick={handleVisitorModalClose} disabled={isAddingVisitors}>
            Cancel
          </Button>,
          <Button 
            key="submit" 
            type="primary" 
            onClick={() => visitorForm.submit()}
            loading={isAddingVisitors}
          >
            Add Visitors
          </Button>
        ]}
        width={600}
      >
        <Form
          form={visitorForm}
          layout="vertical"
          onFinish={handleAddVisitors}
          initialValues={{
            visitors: [{ name: '', phone: '' }]
          }}
        >
          <Form.List name="visitors">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...restField }) => (
                  <Card 
                    key={key} 
                    size="small" 
                    title={`Visitor ${name + 1}`}
                    style={{ marginBottom: 16 }}
                    extra={
                      fields.length > 1 ? (
                        <Button
                          type="text"
                          icon={<MinusCircleOutlined />}
                          onClick={() => remove(name)}
                          disabled={isAddingVisitors}
                        />
                      ) : null
                    }
                  >
                    <Row gutter={16}>
                      <Col span={12}>
                        <Form.Item
                          {...restField}
                          name={[name, 'name']}
                          label="Name (Optional)"
                        >
                          <Input placeholder="Enter visitor name (optional)" disabled={isAddingVisitors} />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item
                          {...restField}
                          name={[name, 'phone']}
                          label="Phone (Optional)"
                        >
                          <Input placeholder="Enter phone number (optional)" disabled={isAddingVisitors} />
                        </Form.Item>
                      </Col>
                    </Row>
                  </Card>
                ))}
                <Form.Item>
                  <Button
                    type="dashed"
                    onClick={() => add()}
                    block
                    icon={<PlusOutlined />}
                    disabled={isAddingVisitors}
                  >
                    Add Another Visitor
                  </Button>
                </Form.Item>
              </>
            )}
          </Form.List>
        </Form>
      </Modal>

      {/* Mobile Menu Drawer */}
      <Drawer
        title="Admin Menu"
        placement="left"
        onClose={() => setIsMobileMenuOpen(false)}
        open={isMobileMenuOpen}
        width={250}
      >
        <div style={{ 
          height: 32, 
          margin: '16px 0', 
          background: 'rgba(0, 0, 0, 0.1)',
          borderRadius: 4,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#333',
          fontWeight: 'bold'
        }}>
          Admin Office
        </div>
        <Menu
          mode="inline"
          selectedKeys={[selectedKey]}
          style={{ borderRight: 0 }}
          onClick={({ key }) => {
            handleMenuClick(key);
            setIsMobileMenuOpen(false);
          }}
        >
          <Menu.Item key="dashboard" icon={<DashboardOutlined />}>
            Dashboard
          </Menu.Item>
          {userAuthorities.length > 0 && hasAuthority('offer:agents:*') && (
            <Menu.SubMenu key="offer" title="Offer" icon={<TeamOutlined />}>
              <Menu.Item key="agents" icon={<TeamOutlined />}>
                Agents
              </Menu.Item>
              <Menu.Item key="offer/merchants" icon={<TeamOutlined />}>
                Merchants
              </Menu.Item>
            </Menu.SubMenu>
          )}
          {userAuthorities.length > 0 && (hasAuthority('newsletter:*') || hasAuthority('files:*')) && (
            <Menu.SubMenu key="office" title="Office" icon={<MailOutlined />}>
              {hasAuthority('newsletter:*') && (
                <Menu.Item key="newsletter" icon={<MailOutlined />}>
                  Newsletter
                </Menu.Item>
              )}
              {hasAuthority('files:*') && (
                <Menu.Item key="files" icon={<FileOutlined />}>
                  Files
                </Menu.Item>
              )}
            </Menu.SubMenu>
          )}
          {userAuthorities.length > 0 && hasAuthority('wa_chats:*') && (
            <Menu.SubMenu key="wa" title="WA" icon={<MessageOutlined />}>
              <Menu.Item key="wa/chats" icon={<MessageOutlined />}>
                Chats
              </Menu.Item>
            </Menu.SubMenu>
          )}
          {userAuthorities.length > 0 && hasAuthority('mbc:*') && (
            <Menu.SubMenu key="mbc" title="MBC" icon={<TeamOutlined />}>
              <Menu.Item key="mbc/agents" icon={<TeamOutlined />}>
                Agents
              </Menu.Item>
            </Menu.SubMenu>
          )}
          {userAuthorities.length > 0 && hasAuthority('users:*') && (
            <Menu.SubMenu key="general" title="General" icon={<UserOutlined />}>
              <Menu.Item key="users" icon={<UserOutlined />}>
                Users
              </Menu.Item>
            </Menu.SubMenu>
          )}
          {userAuthorities.length > 0 && hasAuthority('sf_units:*') && (
            <Menu.SubMenu key="sf" title="SF" icon={<ApartmentOutlined />}>
              <Menu.Item key="sf/units" icon={<ApartmentOutlined />}>
                Units
              </Menu.Item>
            </Menu.SubMenu>
          )}
          {userAuthorities.length > 0 && hasAuthority('winter_offer:*') && (
            <Menu.SubMenu key="winter" title="Winter" icon={<GiftOutlined />}>
              <Menu.Item key="winter/kids" icon={<GiftOutlined />}>
                Kids
              </Menu.Item>
            </Menu.SubMenu>
          )}
          {userAuthorities.length > 0 && hasAuthority('fajr_city:*') && (
            <Menu.Item key="fajr/city" icon={<TeamOutlined />}>
              Mira Hills
            </Menu.Item>
          )}
        </Menu>
      </Drawer>
    </Layout>
  );
};

export default AdminDashboard;